import { CloudOff, RefreshCcw, Wifi } from 'lucide-react';
import { useOfflineQueueCount } from '../../hooks/useOfflineQueueCount';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { useAuth } from '../../lib/auth';
import { cn } from '../../lib/utils';

interface OfflineStatusBannerProps {
  variant?: 'app' | 'login';
}

export function OfflineStatusBanner({ variant = 'app' }: OfflineStatusBannerProps) {
  const isOnline = useOnlineStatus();
  const pendingCount = useOfflineQueueCount();
  const { isOfflineSession } = useAuth();

  if (isOnline && !isOfflineSession && pendingCount === 0) {
    return null;
  }

  const isLoginVariant = variant === 'login';
  const iconClassName = 'mt-0.5 h-4 w-4 flex-shrink-0';

  let tone = 'border-amber-500/30 bg-amber-500/10 text-amber-100';
  let icon = <CloudOff className={iconClassName} />;
  let title = 'Offline Mode';
  let description =
    'HEIMS is running from this device. Supported offline actions will stay on this device until the internet comes back.';

  if (isOnline && pendingCount > 0) {
    tone = 'border-blue-500/30 bg-blue-500/10 text-blue-100';
    icon = <RefreshCcw className={iconClassName} />;
    title = 'Connection restored';
    description = `${pendingCount} pending offline ${pendingCount === 1 ? 'change is' : 'changes are'} waiting to sync.`;
  } else if (isOnline && isOfflineSession) {
    tone = 'border-emerald-500/30 bg-emerald-500/10 text-emerald-100';
    icon = <Wifi className={iconClassName} />;
    title = 'Back online';
    description = 'Refresh or sign in again before making server-confirmed changes.';
  } else if (pendingCount > 0) {
    description = `${pendingCount} pending offline ${pendingCount === 1 ? 'change is' : 'changes are'} saved locally and will need syncing when the internet returns.`;
  }

  return (
    <div
      className={cn(
        'border px-4 py-3 text-sm shadow-sm',
        tone,
        isLoginVariant ? 'mb-5 rounded-xl' : 'border-x-0 border-t-0',
      )}
      role="status"
    >
      <div className="mx-auto flex max-w-6xl gap-3">
        {icon}
        <div>
          <p className="font-semibold">{title}</p>
          <p className={cn('mt-0.5 leading-5', isLoginVariant ? 'text-white/75' : 'opacity-80')}>
            {description}
          </p>
        </div>
      </div>
    </div>
  );
}
